import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip
} from '@mui/material';
import {
  Warning as WarningIcon
} from '@mui/icons-material';

/**
 * StaffingHeader Component
 * 
 * Displays the step indicator, progress information, and status chips.
 * Extracted from StaffingStep for better maintainability.
 */
const StaffingHeader = ({
  formData,
  saving,
  saveError,
  refreshingRates,
  rateUpdateCount,
  metadataUpdateCount,
  itRoster,
  basicDataReady,
  enhancementProgress,
  onRetrySave
}) => {
  return (
    <>
      {/* Step Indicator */}
      <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">
          Step 4 of 5: Staffing Plan
        </Typography>
        
        {/* Status Messages */}
        {saving && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Saving changes...
            </Typography>
          </Box>
        )}
        
        {refreshingRates && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" color="info.main">
              Refreshing member rates...
            </Typography>
          </Box>
        )}
        
        {(rateUpdateCount > 0 || metadataUpdateCount > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Typography variant="caption" color="success.main">
              ✓ Updated {rateUpdateCount > 0 && `${rateUpdateCount} rate${rateUpdateCount !== 1 ? 's' : ''}`}
              {rateUpdateCount > 0 && metadataUpdateCount > 0 && ' and '}
              {metadataUpdateCount > 0 && `${metadataUpdateCount} metadata record${metadataUpdateCount !== 1 ? 's' : ''}`}
            </Typography>
          </Box>
        )}
        
        {saveError && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <WarningIcon color="error" sx={{ fontSize: 16, mr: 1 }} />
            <Typography variant="caption" color="error">
              Auto-save failed.
            </Typography>
            <Button 
              size="small" 
              onClick={onRetrySave}
              sx={{ ml: 1, fontSize: '0.7rem', py: 0, px: 1 }}
            >
              Retry
            </Button>
          </Box>
        )}
      </Box>

            {/* Loading Status - Only show when actively loading */}
      {(!basicDataReady || enhancementProgress.rates === 'loading' || enhancementProgress.silentRefresh === 'loading') && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {!basicDataReady ? 'Loading staffing data...' :
               enhancementProgress.rates === 'loading' ? 'Loading rates...' :
               enhancementProgress.silentRefresh === 'loading' ? 'Updating roles...' :
               'Loading...'}
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Success Status - Only show when everything is loaded */}
      {basicDataReady && enhancementProgress.rates === 'complete' && enhancementProgress.silentRefresh === 'complete' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="success.main">
            ✅ {formData?.milestones?.length || 0} milestones • {formData?.milestones?.reduce((sum, m) => sum + (m.roles?.length || 0), 0) || 0} roles
          </Typography>
        </Box>
      )}
    </>
  );
};

export default StaffingHeader;